/**
 * RFC-110: Spreadsheet Preview Component
 *
 * Renders XLSX/XLS files as interactive tables with sheet tabs.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileSpreadsheet, Download } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@deepguide-ai/ui/button';
import { ScrollArea, ScrollBar } from '@deepguide-ai/ui/scroll-area';
import { cn } from '@/lib/utils';

interface SpreadsheetPreviewProps {
  url: string;
  filename: string;
}

export function SpreadsheetPreview({
  url,
  filename,
}: SpreadsheetPreviewProps) {
  const [activeSheet, setActiveSheet] = useState(0);

  const previewQuery = useQuery({
    ...trpc.evidencePack.getSpreadsheetPreview.queryOptions({ url }),
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  if (previewQuery.isLoading) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading spreadsheet...</span>
      </div>
    );
  }

  if (previewQuery.error || !previewQuery.data || previewQuery.data.sheets.length === 0) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-muted/20 p-8 gap-4">
        <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{filename}</span>
        <span className="text-xs text-destructive">Failed to load preview</span>
        <Button variant="outline" size="sm" asChild>
          <a href={url} download={filename}>
            <Download className="h-4 w-4" />
            Download
          </a>
        </Button>
      </div>
    );
  }

  const { sheets } = previewQuery.data;
  const currentSheet = sheets[activeSheet];

  return (
    <div className="flex h-full w-full min-h-0 min-w-0 flex-col rounded-md border overflow-hidden">
      {/* Sheet tabs */}
      {sheets.length > 1 && (
        <div className="flex gap-1 p-2 border-b bg-muted/30">
          {sheets.map((sheet, idx) => (
            <button
              key={sheet.name}
              onClick={() => setActiveSheet(idx)}
              className={cn(
                'px-3 py-1 text-xs rounded-md transition-colors',
                idx === activeSheet
                  ? 'bg-background shadow-sm font-medium'
                  : 'hover:bg-muted'
              )}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <ScrollArea className="min-h-0 min-w-0 w-full flex-1" type="auto">
        <table className="w-max min-w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
            <tr>
              {currentSheet.headers.map((header, idx) => (
                <th
                  key={idx}
                  className="px-3 py-2 text-left font-medium border-b border-r last:border-r-0 whitespace-nowrap"
                >
                  {header || `Column ${idx + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentSheet.rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-muted/30">
                {row.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className="px-3 py-1.5 border-b border-r last:border-r-0 whitespace-nowrap max-w-[200px] truncate"
                    title={cell}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {currentSheet.totalRows > currentSheet.rows.length && (
          <div className="text-center py-2 text-xs text-muted-foreground border-t">
            Showing {currentSheet.rows.length} of {currentSheet.totalRows} rows
          </div>
        )}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
